import classNames from 'classnames'
import { EltType } from 'legacy/lut/classes/models/partner360/ELT/EltType'
import { TEltFormData } from 'legacy/lut/classes/models/partner360/ELT/IELTFormData'
import EltStoreService from 'legacy/lut/classes/services/EltStoreService'
import { RenderWhen } from 'legacy/lut/components/shared/renderWnen/RenderWhen'
import { isDocType } from 'legacy/lut/components/widgets/public/partner360/ELT/helpers/isDocType'
import { useVersionsTableData } from 'legacy/lut/components/widgets/public/partner360/ELT/ui/versions/hooks/useVersionsSource'
import { downloadFileByUrl } from 'legacy/lut/helpers/files/downloadFileByUrl'
import { useMemo, useState } from 'react'

import { Translation } from 'cng-common/features/translations/components/translation'
import { Button } from 'core-components/library/button/Button'
import { Dropdown } from 'core-components/library/dropdown'
import { Loader } from 'core-components/library/loader'
import { Static } from 'core-components/types'
import { Crud } from 'core/types/Crud'

import { isExistsChangedSteps } from '../../../helpers/changedStep/isExistsChangedSteps'
import { getFormedEltTranslation } from '../../../helpers/getFormedEltTranslation'
import { IChangedSteps } from '../../../models/IChangedSteps'
import { Alert } from '../../styledComponents/alert/Alert'

import { fractionDigits } from './configs'

import styles from './ExportStep.module.scss'

interface IProps {
  step?: number
  entryId?: number
  changedSteps: IChangedSteps
  updateFormData: (field: string) => (value: string) => void
  formData: TEltFormData
  eltType: EltType
  onSave: (step?: number) => Promise<void>
}

export const ExportStep = ({
  step,
  entryId,
  changedSteps,
  updateFormData,
  formData,
  eltType,
  onSave,
}: IProps) => {
  /** -------------------- State ----------------------- */

  const [wip, setWip] = useState(false)
  const [isError, setIsError] = useState(false)

  /** -------------------- Variables ----------------------- */
  const { refetch: refetchVersionTable } = useVersionsTableData()

  const fractionDigitsCrud = useMemo(() => {
    const crud = new Crud({
      fetcher: new Static(fractionDigits),
      keyField: 'key',
    })
    return crud
  }, [])

  const isExportDocx = useMemo(() => {
    return isDocType(eltType)
  }, [eltType])

  const exportPptxTranslationKey = useMemo(() => {
    return isDocType(eltType) ? 'export-btn-pptx' : 'export-btn'
  }, [eltType])

  /** -------------------- User actions handlers ----------------------- */

  const createDocument = async (format?: string) => {
    if (entryId) {
      await onSave(step)
      setWip(true)
      const path = await EltStoreService.createDocument(entryId, format)
      if (path) {
        downloadFileByUrl(path, 'elt-form')
        refetchVersionTable()
      } else {
        setIsError(true)
      }
    }
    setWip(false)
  }

  /** -------------------- Render ----------------------- */

  if (!entryId) {
    return (
      <div className={classNames(styles.center, styles.notReady)}>
        <Translation span text={getFormedEltTranslation('document-not-ready-to-export')} />
      </div>
    )
  }

  if (wip) {
    return <Loader />
  }

  if (isError) {
    return (
      <div className={classNames(styles.center, styles.error)}>
        <Translation span text={getFormedEltTranslation('export-something-went-wrong')} />
      </div>
    )
  }

  return (
    <div className={styles.item}>
      <div className={styles.itemFirst}>
        <Alert text={getFormedEltTranslation(`digit-text`)} />
        <Dropdown
          classes={{
            controlClasses: {
              root: styles.dropdown,
            },
          }}
          caption={'caption'}
          onChange={updateFormData('FractionDigits')}
          source={fractionDigitsCrud}
          value={formData.FractionDigits}
        />
        <RenderWhen condition={!!formData.FractionDigits}>
          <Alert>
            <Translation
              htmlAvailable
              text={getFormedEltTranslation(`digit-text-${formData.FractionDigits}`)}
            />
          </Alert>
        </RenderWhen>
      </div>
      <div className={styles.itemLast}>
        <RenderWhen condition={isExistsChangedSteps(changedSteps)}>
          <Alert text={getFormedEltTranslation('export-warning')} margin={{ bottom: 'xl' }} />
        </RenderWhen>
        <div className={styles.btnList}>
          <RenderWhen condition={isExportDocx}>
            <div className={styles.btnContainer}>
              <i className={classNames(`kbase kbase-document`, styles.exportIcon)} />
              <Button
                disabled={isExistsChangedSteps(changedSteps)}
                className={styles.btn}
                onClick={() => createDocument('docx')}
              >
                <Translation text={getFormedEltTranslation('export-btn-docx')} />
              </Button>
            </div>
          </RenderWhen>
          <div className={styles.btnContainer}>
            <i className={classNames(`kbase kbase-ppt`, styles.exportIcon)} />
            <Button
              disabled={isExistsChangedSteps(changedSteps)}
              className={styles.btn}
              onClick={() => createDocument('pptx')}
            >
              <Translation text={getFormedEltTranslation(exportPptxTranslationKey)} />
            </Button>
          </div>
        </div>
      </div>
    </div>
  )
}
